import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Award,
  BookOpen,
  Briefcase,
  Building,
  CalendarDays,
  Clock,
  Coffee,
  Compass,
  Edit2,
  FileText,
  Globe,
  GraduationCap,
  GripVertical,
  Heart,
  HelpCircle,
  Inbox,
  Info,
  Lightbulb,
  Loader2,
  MapPin,
  MessageSquare,
  Plus,
  Search,
  Settings,
  Star,
  Target,
  Trash2,
  Users
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';

interface StarterCard {
  id: number;
  text: string;
  text_en?: string;
  icon: string;
  is_active: boolean;
  sort_order: number;
  createdAt: string;
  updatedAt: string;
}

const AVAILABLE_ICONS = [
  { name: 'CalendarDays', icon: CalendarDays },
  { name: 'MapPin', icon: MapPin },
  { name: 'Globe', icon: Globe },
  { name: 'Info', icon: Info },
  { name: 'MessageSquare', icon: MessageSquare },
  { name: 'HelpCircle', icon: HelpCircle },
  { name: 'BookOpen', icon: BookOpen },
  { name: 'GraduationCap', icon: GraduationCap },
  { name: 'Building', icon: Building },
  { name: 'Users', icon: Users },
  { name: 'Clock', icon: Clock },
  { name: 'FileText', icon: FileText },
  { name: 'Search', icon: Search },
  { name: 'Settings', icon: Settings },
  { name: 'Star', icon: Star },
  { name: 'Heart', icon: Heart },
  { name: 'Lightbulb', icon: Lightbulb },
  { name: 'Target', icon: Target },
  { name: 'Compass', icon: Compass },
  { name: 'Award', icon: Award },
  { name: 'Briefcase', icon: Briefcase },
  { name: 'Coffee', icon: Coffee }
];

const iconComponent = (name: string) =>
  AVAILABLE_ICONS.find((entry) => entry.name === name)?.icon || MessageSquare;

const AdminCards = () => {
  const { t } = useTranslation();
  const [cards, setCards] = useState<StarterCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingCard, setEditingCard] = useState<StarterCard | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cardToDelete, setCardToDelete] = useState<StarterCard | null>(null);
  const [draggedCard, setDraggedCard] = useState<StarterCard | null>(null);
  const [dragOverCard, setDragOverCard] = useState<StarterCard | null>(null);
  const [formText, setFormText] = useState('');
  const [formTextEn, setFormTextEn] = useState('');
  const [formIcon, setFormIcon] = useState('MessageSquare');
  const [formIsActive, setFormIsActive] = useState(true);

  const fetchCards = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/cards/all', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error('Load failed');
      setCards(data.data || []);
    } catch (error) {
      console.error('Error fetching cards:', error);
      toast.error(t('admin.starterCards.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCards();
  }, []);

  const resetForm = () => {
    setEditingCard(null);
    setFormText('');
    setFormTextEn('');
    setFormIcon('MessageSquare');
    setFormIsActive(true);
  };

  const openCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEdit = (card: StarterCard) => {
    setEditingCard(card);
    setFormText(card.text);
    setFormTextEn(card.text_en || '');
    setFormIcon(card.icon);
    setFormIsActive(card.is_active);
    setDialogOpen(true);
  };

  const saveCard = async () => {
    if (!formText.trim()) {
      toast.error(t('admin.starterCards.required'));
      return;
    }
    try {
      setSaving(true);
      const response = await fetch(editingCard ? '/api/cards/update' : '/api/cards/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(editingCard ? { id: editingCard.id, is_active: formIsActive } : {}),
          text: formText.trim(),
          text_en: formTextEn.trim() || undefined,
          icon: formIcon
        })
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Save failed');
      toast.success(editingCard ? t('admin.starterCards.updated') : t('admin.starterCards.created'));
      setDialogOpen(false);
      resetForm();
      await fetchCards();
    } catch (error) {
      console.error('Error saving card:', error);
      toast.error(t('admin.starterCards.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (card: StarterCard) => {
    try {
      const response = await fetch('/api/cards/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: card.id, is_active: !card.is_active })
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error('Update failed');
      setCards((current) => current.map((entry) => entry.id === card.id ? { ...entry, is_active: !entry.is_active } : entry));
    } catch (error) {
      console.error('Error updating card:', error);
      toast.error(t('admin.starterCards.saveError'));
    }
  };

  const deleteCard = async () => {
    if (!cardToDelete) return;
    try {
      setSaving(true);
      const response = await fetch('/api/cards/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: cardToDelete.id })
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error('Delete failed');
      toast.success(t('admin.starterCards.deleted'));
      setCardToDelete(null);
      await fetchCards();
    } catch (error) {
      console.error('Error deleting card:', error);
      toast.error(t('admin.starterCards.deleteError'));
    } finally {
      setSaving(false);
    }
  };

  const reorderCards = async (target: StarterCard) => {
    if (!draggedCard || draggedCard.id === target.id) return;
    const reordered = [...cards];
    const from = reordered.findIndex((entry) => entry.id === draggedCard.id);
    const to = reordered.findIndex((entry) => entry.id === target.id);
    reordered.splice(from, 1);
    reordered.splice(to, 0, draggedCard);
    const ordered = reordered.map((entry, index) => ({ ...entry, sort_order: index }));
    setCards(ordered);
    setDraggedCard(null);
    setDragOverCard(null);

    try {
      const response = await fetch('/api/cards/reorder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ card_orders: ordered.map((entry) => ({ id: entry.id, sort_order: entry.sort_order })) })
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error('Reorder failed');
    } catch (error) {
      console.error('Error reordering cards:', error);
      toast.error(t('admin.starterCards.reorderError'));
      await fetchCards();
    }
  };

  const activeCount = cards.filter((card) => card.is_active).length;

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('admin.starterCards.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('admin.starterCards.description')}</p>
        </div>
        <Button onClick={openCreate}><Plus className="mr-2 h-4 w-4" />{t('admin.starterCards.new')}</Button>
      </div>

      <section className="border-b border-border">
        <div className="flex items-center justify-between gap-3 border-b border-border py-4">
          <p className="text-sm text-muted-foreground">{t('admin.starterCards.count', { active: activeCount, total: cards.length })}</p>
          <p className="text-xs text-muted-foreground">{t('admin.starterCards.dragHint')}</p>
        </div>

        <div className="overflow-x-auto py-3">
          {loading ? (
            <div className="space-y-2">{Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} className="h-16 w-full" />)}</div>
          ) : cards.length === 0 ? (
            <div className="flex min-h-64 flex-col items-center justify-center text-center">
              <Inbox className="mb-3 h-8 w-8 text-muted-foreground/60" />
              <p className="font-medium">{t('admin.starterCards.empty')}</p>
              <p className="mt-1 text-sm text-muted-foreground">{t('admin.starterCards.emptyDescription')}</p>
            </div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead className="w-10"><span className="sr-only">{t('admin.starterCards.order')}</span></TableHead>
                <TableHead className="w-[52%]">{t('admin.starterCards.card')}</TableHead>
                <TableHead>{t('admin.starterCards.status')}</TableHead>
                <TableHead className="text-right">{t('admin.starterCards.actions')}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {cards.map((card) => {
                  const Icon = iconComponent(card.icon);
                  return (
                    <TableRow
                      key={card.id}
                      draggable
                      onDragStart={() => setDraggedCard(card)}
                      onDragOver={(event) => { event.preventDefault(); setDragOverCard(card); }}
                      onDragLeave={() => setDragOverCard(null)}
                      onDrop={(event) => { event.preventDefault(); reorderCards(card); }}
                      onDragEnd={() => { setDraggedCard(null); setDragOverCard(null); }}
                      className={dragOverCard?.id === card.id ? 'bg-muted/40' : undefined}
                    >
                      <TableCell className="py-4"><GripVertical className="h-4 w-4 cursor-grab text-muted-foreground" /></TableCell>
                      <TableCell className="py-4"><div className="flex min-w-0 items-center gap-3"><Icon className="h-4 w-4 shrink-0 text-muted-foreground" /><span className="max-w-[620px] truncate font-medium" title={card.text}>{card.text}</span></div></TableCell>
                      <TableCell className="py-4"><div className="flex items-center gap-2"><Switch checked={card.is_active} onCheckedChange={() => toggleActive(card)} aria-label={t('admin.starterCards.toggle')} /><span className="text-sm text-muted-foreground">{card.is_active ? t('admin.starterCards.active') : t('admin.starterCards.inactive')}</span></div></TableCell>
                      <TableCell className="py-4 text-right"><div className="inline-flex gap-2"><Button size="icon" variant="outline" className="h-8 w-8" onClick={() => openEdit(card)} aria-label={t('admin.starterCards.edit')} title={t('admin.starterCards.edit')}><Edit2 className="h-4 w-4" /></Button><Button size="icon" variant="destructive" className="h-8 w-8" onClick={() => setCardToDelete(card)} aria-label={t('admin.starterCards.delete')} title={t('admin.starterCards.delete')}><Trash2 className="h-4 w-4" /></Button></div></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </section>

      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>{editingCard ? t('admin.starterCards.editTitle') : t('admin.starterCards.createTitle')}</DialogTitle><DialogDescription>{t('admin.starterCards.dialogDescription')}</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label htmlFor="starter-card-sr">{t('admin.starterCards.serbian')}</Label><Textarea id="starter-card-sr" value={formText} onChange={(event) => setFormText(event.target.value)} rows={3} /></div>
            <div className="space-y-2"><Label htmlFor="starter-card-en">{t('admin.starterCards.englishOptional')}</Label><Textarea id="starter-card-en" value={formTextEn} onChange={(event) => setFormTextEn(event.target.value)} rows={3} /></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2"><Label>{t('admin.starterCards.icon')}</Label><Select value={formIcon} onValueChange={setFormIcon}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{AVAILABLE_ICONS.map((entry) => { const Icon = entry.icon; return <SelectItem key={entry.name} value={entry.name}><span className="flex items-center gap-2"><Icon className="h-4 w-4" />{t(`admin.starterCards.iconOptions.${entry.name}`, { defaultValue: entry.name })}</span></SelectItem>; })}</SelectContent></Select></div>
              {editingCard ? <div className="space-y-2"><Label>{t('admin.starterCards.status')}</Label><div className="flex h-10 items-center gap-2"><Switch checked={formIsActive} onCheckedChange={setFormIsActive} /><span className="text-sm text-muted-foreground">{formIsActive ? t('admin.starterCards.active') : t('admin.starterCards.inactive')}</span></div></div> : null}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>{t('admin.starterCards.cancel')}</Button><Button onClick={saveCard} disabled={saving}>{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{t('admin.starterCards.save')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(cardToDelete)} onOpenChange={(open) => !open && setCardToDelete(null)}>
        <DialogContent className="sm:max-w-md"><DialogHeader><DialogTitle>{t('admin.starterCards.deleteTitle')}</DialogTitle><DialogDescription>{t('admin.starterCards.deleteDescription')}</DialogDescription></DialogHeader><DialogFooter><Button variant="outline" onClick={() => setCardToDelete(null)} disabled={saving}>{t('admin.starterCards.cancel')}</Button><Button variant="destructive" onClick={deleteCard} disabled={saving}>{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}{t('admin.starterCards.delete')}</Button></DialogFooter></DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCards;
